import React, { useEffect, useRef, useState } from "react";
import { ProfileRow } from "../ProfileRow";
import { useTranslation } from "@helpers/translate";
import { alpha3ToAlpha2 } from "@utils/countryCodeMap";
import CountrySelectProfile from "../CountrySelectProfile";
import "./index.sass";

const normalizeAlpha2 = (code) => {
  if (!code || typeof code !== "string") return "";

  const normalizedCode = code.trim().toUpperCase();

  return normalizedCode.length === 2 ? normalizedCode : "";
};

const toAlpha2CountryCode = (countryValue) => {
  if (!countryValue) return "";

  if (typeof countryValue === "string") {
    const normalizedAlpha2 = normalizeAlpha2(countryValue);

    if (normalizedAlpha2) {
      return normalizedAlpha2;
    }

    const alpha3Code = countryValue.trim().toUpperCase();

    if (alpha3Code.length === 3) {
      return normalizeAlpha2(alpha3ToAlpha2[alpha3Code]);
    }

    return "";
  }

  const directAlpha2 =
    normalizeAlpha2(countryValue.code_alpha2) ||
    normalizeAlpha2(countryValue.alpha2) ||
    normalizeAlpha2(countryValue.iso2) ||
    normalizeAlpha2(countryValue.code2) ||
    normalizeAlpha2(countryValue.code);

  if (directAlpha2) {
    return directAlpha2;
  }

  const alpha3Code =
    countryValue.code_alpha3 ||
    countryValue.alpha3 ||
    countryValue.iso3 ||
    countryValue.code;

  if (
    typeof alpha3Code === "string" &&
    alpha3Code.trim().length === 3
  ) {
    return normalizeAlpha2(
      alpha3ToAlpha2[alpha3Code.trim().toUpperCase()],
    );
  }

  return "";
};

export const CountryRow = ({
  country,
  locale,
  countries = [],
  handleSaveField,
  savingField,
}) => {
  const { __ } = useTranslation();

  const countrySelectRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState(() =>
    toAlpha2CountryCode(country),
  );

  const [countrySelectOpen, setCountrySelectOpen] = useState(false);

  useEffect(() => {
    setSelectedCountry(toAlpha2CountryCode(country));
  }, [country]);

  const handleCountryChange = (newValue) => {
    const countryCode = toAlpha2CountryCode(newValue);

    if (!countryCode || countryCode === selectedCountry) {
      return;
    }

    setSelectedCountry(countryCode);
    handleSaveField("country", countryCode);
  };

  const handleToggleCountrySelect = () => {
    if (savingField === "country") return;

    countrySelectRef.current?.toggle();
  };

  const value = (
    <div className="profile-row__edit-input profile-row__country-input">
      <CountrySelectProfile
        ref={countrySelectRef}
        countries={countries}
        value={selectedCountry}
        locale={locale}
        placeholder={__("profile.country_region")}
        onChange={handleCountryChange}
        onOpenChange={setCountrySelectOpen}
        disabled={savingField === "country"}
        showArrow={false}
        triggerSelector=".country-row__select-button"
      />
    </div>
  );

  const actions = (
    <button
      type="button"
      onClick={handleToggleCountrySelect}
      disabled={savingField === "country"}
      className="profile-action__button country-row__select-button"
      aria-label={__("profile.country_region")}
      aria-expanded={countrySelectOpen}
    >
      <span
        className={`country-row__select-chevron ${
          countrySelectOpen
            ? "country-row__select-chevron--open"
            : ""
        }`}
        aria-hidden="true"
      />
    </button>
  );

  return (
    <div className="country-row">
      <ProfileRow
        label={__("profile.country_region")}
        value={value}
        actions={actions}
      />
    </div>
  );
};
